import { useEffect, useMemo, useState } from 'react';
import { Container, Table, Button, Alert, Badge, Spinner, Form, Nav, Row, Col, Card } from 'react-bootstrap';
import { Plus } from 'react-bootstrap-icons';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { Case } from '../../types';
import { Invoice, OrgStripeStatus } from '../../types/admin';
import { fetchInvoices } from '../../api/admin';
import { fetchCases } from '../../api/cases';
import { useAdmin } from '../../contexts/AdminContext';
import InvoiceModal from '../../components/admin/InvoiceModal';
import { money, formatDay, STATUS_VARIANTS } from '../../utils/invoiceFormat';

const STATUS_TABS = ['all', 'draft', 'open', 'paid', 'void'] as const;

const todayIso = () => new Date().toISOString().slice(0, 10);

const isOverdue = (inv: Invoice) => inv.status === 'open' && !!inv.due_date && inv.due_date < todayIso();

export default function Invoices() {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const { isSuperAdmin } = useAdmin();
  const [searchParams] = useSearchParams();
  const caseFilter = searchParams.get('case_id') || '';

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stripe, setStripe] = useState<Record<string, OrgStripeStatus>>({});
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [statusTab, setStatusTab] = useState<typeof STATUS_TABS[number]>('all');
  const [orgFilter, setOrgFilter] = useState('');
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<Invoice | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [invoiceData, caseResponse] = await Promise.all([
        fetchInvoices(token, { case_id: caseFilter || undefined }),
        fetchCases(token, { all: isSuperAdmin }),
      ]);
      setInvoices(invoiceData.invoices);
      setStripe(invoiceData.stripe);
      const caseList: Case[] = caseResponse.data.cases || caseResponse.data;
      setCases([...caseList].sort((a, b) => (a.case_number || '').localeCompare(b.case_number || '')));
    } catch (err: any) {
      console.error('Error loading invoices:', err);
      setError(err.response?.data?.error || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, caseFilter, isSuperAdmin]);

  const orgName = (orgId: string) => stripe[orgId]?.organization_name || orgId;

  // Super admins load every org's cases; the New Invoice picker only offers the
  // org they've filtered to (their own org by default), plus a ?case_id= deep link.
  const ownOrg = (auth.user?.profile?.website as string) || '';
  const caseOptions = useMemo(() => {
    const org = orgFilter || ownOrg;
    if (!isSuperAdmin || !org) return cases;
    return cases.filter(c => c.organization_id === org || c.case_id === caseFilter);
  }, [cases, isSuperAdmin, orgFilter, ownOrg, caseFilter]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter(inv =>
      (statusTab === 'all' || inv.status === statusTab) &&
      (!orgFilter || inv.organization_id === orgFilter) &&
      (!q || [inv.stripe_number, inv.case_number, inv.case_title, inv.customer_name, inv.customer_email, inv.memo]
        .some(v => (v || '').toLowerCase().includes(q)))
    );
  }, [invoices, statusTab, orgFilter, search]);

  const totals = useMemo(() => {
    const scoped = invoices.filter(inv => !orgFilter || inv.organization_id === orgFilter);
    const open = scoped.filter(inv => inv.status === 'open');
    return {
      outstanding: open.reduce((s, inv) => s + (inv.amount_remaining ?? inv.subtotal), 0),
      overdue: open.filter(isOverdue).reduce((s, inv) => s + (inv.amount_remaining ?? inv.subtotal), 0),
      paid: scoped.filter(inv => inv.status === 'paid').reduce((s, inv) => s + (inv.amount_paid || 0), 0),
      drafts: scoped.filter(inv => inv.status === 'draft').length,
    };
  }, [invoices, orgFilter]);

  // Stripe connection banner: org admins see their own org; super admins see
  // the org they've filtered to.
  const bannerOrg = isSuperAdmin ? orgFilter : Object.keys(stripe)[0];
  const bannerStripe = bannerOrg ? stripe[bannerOrg] : undefined;

  const openInvoice = (inv: Invoice | null) => {
    setSelected(inv);
    setShowModal(true);
  };

  const handleSaved = (inv: Invoice | null, message: string) => {
    if (inv) {
      setInvoices(list => [inv, ...list.filter(i => i.invoice_id !== inv.invoice_id)]
        .sort((a, b) => b.created_at.localeCompare(a.created_at)));
    } else if (selected) {
      setInvoices(list => list.filter(i => i.invoice_id !== selected.invoice_id));
    }
    if (!message) {
      // Saved but the follow-up step failed; keep the modal open on the saved copy.
      setSelected(inv);
      return;
    }
    setShowModal(false);
    setSelected(null);
    setSuccess(message);
    setTimeout(() => setSuccess(null), 5000);
  };

  return (
    <Container className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Invoices</h2>
          <p className="text-muted mb-0">Create, send, and track per-case client invoices paid through Stripe</p>
        </div>
        <Button variant="primary" onClick={() => openInvoice(null)} disabled={loading}>
          <Plus className="me-2" />
          New Invoice
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {!loading && bannerOrg && bannerStripe && !bannerStripe.configured && (
        <Alert variant="warning">
          <strong>Stripe isn't connected</strong> for {orgName(bannerOrg)}. You can create drafts, but finalizing and
          sending invoices needs a connected Stripe account — contact CaseTrail support to set it up.
        </Alert>
      )}
      {!loading && bannerStripe?.configured && !bannerStripe.livemode && (
        <Alert variant="info" className="py-2">
          Stripe is connected in <strong>test mode</strong> for {orgName(bannerOrg!)} — invoices won't charge real money.
        </Alert>
      )}
      {!loading && bannerStripe?.configured && bannerStripe.livemode && (
        <div className="small text-muted mb-3">
          Billing through Stripe account {bannerStripe.account_id || ''} (live)
        </div>
      )}

      {caseFilter && (
        <Alert variant="light" className="border py-2">
          Showing invoices for one case. <a href="/admin/invoices">Show all invoices</a>
        </Alert>
      )}

      <Row className="g-3 mb-4">
        {[
          { label: 'Outstanding', value: money(totals.outstanding) },
          { label: 'Overdue', value: money(totals.overdue), tone: totals.overdue > 0 ? 'text-danger' : '' },
          { label: 'Paid', value: money(totals.paid), tone: 'text-success' },
          { label: 'Drafts', value: String(totals.drafts) },
        ].map(s => (
          <Col key={s.label} xs={6} md={3}>
            <Card body className="h-100">
              <div className="small text-muted text-uppercase">{s.label}</div>
              <div className={`fs-5 fw-semibold ${s.tone || ''}`}>{s.value}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center mb-3">
        <Nav variant="pills" activeKey={statusTab} onSelect={k => setStatusTab((k as typeof STATUS_TABS[number]) || 'all')}>
          {STATUS_TABS.map(s => (
            <Nav.Item key={s}>
              <Nav.Link eventKey={s} className="text-capitalize py-1">{s}</Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <div className="d-flex gap-2">
          {isSuperAdmin && (
            <Form.Select size="sm" style={{ width: 220 }} value={orgFilter} onChange={e => setOrgFilter(e.target.value)}>
              <option value="">All organizations</option>
              {Object.entries(stripe)
                .sort((a, b) => a[1].organization_name.localeCompare(b[1].organization_name))
                .map(([id, s]) => (
                  <option key={id} value={id}>{s.organization_name || id}{s.configured ? '' : ' (no Stripe)'}</option>
                ))}
            </Form.Select>
          )}
          <Form.Control size="sm" style={{ width: 240 }} placeholder="Search client, case, invoice #…"
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading invoices...</p>
        </div>
      ) : (
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Case</th>
              <th>Bill to</th>
              {isSuperAdmin && !orgFilter && <th>Organization</th>}
              <th className="text-end">Amount</th>
              <th>Status</th>
              <th>Due</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(inv => (
              <tr key={inv.invoice_id} style={{ cursor: 'pointer' }} onClick={() => openInvoice(inv)}>
                <td>
                  {inv.stripe_number ? <strong>{inv.stripe_number}</strong> : <span className="text-muted">Draft</span>}
                  {inv.memo && <div className="small text-muted text-truncate" style={{ maxWidth: 220 }}>{inv.memo}</div>}
                </td>
                <td>
                  <div>{inv.case_number}</div>
                  <div className="small text-muted">{inv.case_title}</div>
                </td>
                <td>
                  <div>{inv.customer_name}</div>
                  <div className="small text-muted">{inv.customer_email}</div>
                </td>
                {isSuperAdmin && !orgFilter && <td className="small">{orgName(inv.organization_id)}</td>}
                <td className="text-end">
                  {money(inv.subtotal)}
                  {inv.status === 'open' && (inv.amount_paid || 0) > 0 && (
                    <div className="small text-muted">{money(inv.amount_remaining)} left</div>
                  )}
                </td>
                <td>
                  <Badge bg={STATUS_VARIANTS[inv.status]}>{inv.status}</Badge>
                  {isOverdue(inv) && <Badge bg="danger" className="ms-1">overdue</Badge>}
                  {inv.status !== 'draft' && inv.livemode === false && <Badge bg="warning" text="dark" className="ms-1">test</Badge>}
                </td>
                <td className={isOverdue(inv) ? 'text-danger' : ''}>{formatDay(inv.due_date)}</td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin && !orgFilter ? 7 : 6} className="text-center text-muted py-4">
                  {invoices.length === 0 ? 'No invoices yet — create one with "New Invoice".' : 'No invoices match these filters.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <InvoiceModal
        show={showModal}
        invoice={selected}
        cases={caseOptions}
        stripe={stripe}
        initialCaseId={caseFilter}
        onClose={() => { setShowModal(false); setSelected(null); }}
        onSaved={handleSaved}
      />
    </Container>
  );
}
